import { Button } from 'js/design_system/Button';
import React, { useCallback } from 'react';
import fetchWithCsrf from 'js/utils/fetch_with_csrf';
import routes from 'js/core_pre_webpack/routes';
import {
  trackClick,
  Options as ClickTrackingOptions,
} from 'js/utils/trackClick';
import { RouteOptions } from 'js/core_pre_webpack/routes';
import { consoleError } from 'js/utils/console';
import { imageUrl } from 'js/utils/image_url';
import { RegistrationParams } from '../types';

type AppleResponse = {
  authorization: {
    state?: string;
    code: string;
    id_token: string;
  };
  user?: {
    email: string;
    name: {
      firstName: string;
      lastName: string;
    };
  };
};

type Props = {
  onAttempt?: () => void;
  onAuthorized?: () => void;
  onSuccess?: (redirect?: string) => void;
  onError: (error?: { errorMessage?: string; errorCodes?: string[] }) => void;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  clickTrackingContext?: Partial<ClickTrackingOptions>;
  buttonText: string;
  registrationParams: RegistrationParams;
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onError'>;

const genHexString = (len: number) => {
  let output = '';
  for (let i = 0; i < len; ++i) {
    output += Math.floor(Math.random() * 16).toString(16);
  }
  return output;
};

// redirectURI has to be a valid URL, and it might have to be a URL that we've configured with Apple.
// I don't have access to that so I don't know. But it doesn't really matter because it doesn't get used.
// Instead we set `usePopup: true` below in appleSignIn() and that makes it return the relevant info
// instead of POSTing it to the redirectURI like the docs say it does.
// routes.session_url() causes an unsightly console error if you don't provide the id attribute,
// but providing it causes the url to have a slash on the end, which Apple won't accept.
// So we do this ridiculous thing to stub out the necessary attribute then remove the trailing slash.
const redirectURI = `https:${routes.session_url({ id: '' })}`.slice(0, -1);
const appleScopes = 'name email';
const initApple = async () => {
  const appleClientId = window.loginModal?.appleClientId;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  await (window as any).AppleID.auth.init({
    clientId: appleClientId,
    scope: appleScopes,
    redirectURI: redirectURI,
    state: genHexString(32),
  });
};

const appleSignIn = async () => {
  try {
    const resp: AppleResponse = await window.AppleID.auth.signIn({
      clientId: window.loginModal?.appleClientId,
      redirectURI: redirectURI,
      scope: appleScopes,
      state: 'state',
      // usePopup makes it return the response from the JS function instead of just posting it to the redirectURI
      usePopup: true,
    });
    return resp;
  } catch (error) {
    console.error(error);
  }
};

export const ContinueWithAppleButton: React.FC<Props> = (props) => {
  const {
    onAttempt,
    onAuthorized,
    onSuccess,
    onError,
    disabled,
    setLoading,
    clickTrackingContext,
    buttonText,
    registrationParams,
    ...buttonProps
  } = props;

  const handleAppleAuthButtonClick = useCallback(async () => {
    if (disabled) {
      return;
    }
    try {
      onAttempt && onAttempt();
      setLoading(true);
      trackClick({
        source: 'login_modal_apple_oauth_button',
        ...clickTrackingContext,
      });
      await initApple();
      const resp = await appleSignIn();
      onAuthorized && onAuthorized();
      const data = {
        provider: 'apple',
        code: resp?.authorization.code,
        id_token: resp?.authorization.id_token,
        user: resp?.user,
        apple_web_sign_in: true,
        ...registrationParams,
      };
      const serverResp = await fetchWithCsrf(
        routes.api_v0_register_path({ subdomain_param: 'api' }),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        },
      );
      const respBody = await serverResp.json();
      if (respBody.success) {
        onSuccess && onSuccess(respBody.redirect);
      } else if (respBody.redirect) {
        // This happens if the user created their account with a different OAuth provider
        // than the one they're attempting to log in with.
        window.location.href = respBody.redirect;
      } else if (respBody.error_codes[0] === 'account_already_exists') {
        const redirectParams: RouteOptions = {
          provider: 'Apple',
          email_was_taken: true,
        };
        if (resp?.user?.email) {
          // without the conditional it might prefill the email input on the login page with `undefined` instead of leaving it blank
          redirectParams.login = resp.user.email;
        }
        window.location.href = routes.login_path(redirectParams);
      } else if (
        respBody.errors['user[first_name]'] ||
        respBody.errors['user[last_name]']
      ) {
        onError({
          errorMessage:
            'Your Apple account is missing a first or last name. Please add one to your Apple account and try again, or use a different login method.',
        });
      } else if (respBody.error_codes) {
        onError({ errorCodes: respBody.error_codes });
      } else {
        onError();
      }
    } catch (e) {
      consoleError(e as Error);
      setLoading(false);
    }
  }, [
    disabled,
    onAttempt,
    setLoading,
    clickTrackingContext,
    onAuthorized,
    registrationParams,
    onSuccess,
    onError,
  ]);

  return (
    <Button
      secondary
      onClick={handleAppleAuthButtonClick}
      disabled={disabled}
      {...buttonProps}
    >
      <img src={imageUrl('apple/apple-black.svg')} />
      {buttonText}
    </Button>
  );
};
